import {useEffect, useState} from "react";
import {useInView} from "~/new-design/hooks";

/**
 * Breathing dusk landscape — the screen the welcome splash lands on.
 * Ported from the "Bounce Landscape" Claude Design file: a settled scene
 * (haze, light rays, realistic cloud strata, fog wisps, hills, mist) that
 * breathes with a slow idle motion. No entrance choreography beyond the
 * cloud fade-in handoff from the splash.
 */

// The design runs its idle landscape at 1.6x. Drop to 1 for the original calmer tempo.
const IDLE_SPEED = 1.6;
const SLOW = 1 / IDLE_SPEED;
const d = (sec: number) => `${+(sec * SLOW).toFixed(3)}s`;
// Cap continuous/looping ambient animations to ~30fps by stepping the timeline.
// N = round(30fps × real iteration duration in seconds). One-shot intros stay smooth.
const TARGET_FPS = 30;
const stepFor = (sec: number) => `steps(${Math.max(1, Math.round(TARGET_FPS * sec * SLOW))})`;

const palette = {
  duskTop: "#D7E5F3",
  duskMid: "#D3DBEF",
  duskDeep: "#CECFE8",
  duskLow: "#ECE5DB",
  horizon: "#F7E7CC",
  hillFar: "#9FAFB4",
  hillMid: "#6E8590",
  sunGlow: "#FFF7ED",
  mist: "#F6F1EE",
  cloudHi: "rgba(255,250,238,0.98)",
  cloudWarm: "#F4E8D4",
  cloudLo: "#B7AFCB",
  cloudSh: "#9A90B3",
};

const KEYFRAMES = `
  @keyframes ls-sceneBreathe { from { transform: scale(1); } to { transform: scale(1.018); } }
  @keyframes ls-skyShift { from { transform: translateX(-3.5%); opacity: 0.22; } to { transform: translateX(3.5%); opacity: 0.46; } }
  @keyframes ls-hazeBreathe { from { opacity: 0.45; } to { opacity: 0.66; } }
  @keyframes ls-haloBreathe { from { opacity: 1; } to { opacity: 0.86; } }
  @keyframes ls-glowBreathe { from { opacity: 0.55; scale: 1; } to { opacity: 0.62; scale: 1.04; } }
  @keyframes ls-rayWax {
    0%   { opacity: 0.07; } 18%  { opacity: 0.34; } 34%  { opacity: 0.13; }
    52%  { opacity: 0.46; } 70%  { opacity: 0.18; } 86%  { opacity: 0.32; } 100% { opacity: 0.07; }
  }
  @keyframes ls-cloudIn { from { opacity: 0; } to { opacity: var(--co); } }
  @keyframes ls-cloudRise { from { transform: translateY(4vh) scaleY(0.8); } to { transform: translateY(0) scaleY(0.8); } }
  @keyframes ls-cloudBreathe {
    0%   { scale: 1;     translate: 0 0; }
    28%  { scale: 1.02;  translate: -1.4% -0.5vh; }
    54%  { scale: 1.03;  translate: 1.4% -0.9vh; }
    78%  { scale: 1.018; translate: 2% -0.4vh; }
    100% { scale: 1;     translate: 0 0; }
  }
  @keyframes ls-fogSpin { to { transform: rotate(360deg); } }
  @keyframes ls-fogSway { from { transform: rotate(-26deg); } to { transform: rotate(26deg); } }
  @keyframes ls-wispPulse { from { opacity: var(--fa); } to { opacity: var(--fb); } }
  @keyframes ls-mistDrift { from { transform: translateX(-1.5%); } to { transform: translateX(1.5%); } }
`;

// ————— cloud strata: each band's morphology (texture + warp) is generated by
// an SVG turbulence/displacement filter; shapes are soft ellipse stacks. —————
type CloudFilterDef = {
  id: string;
  blur1: string;
  bf1: string;
  seed1: number;
  mAlpha: string;
  bf2: string;
  seed2: number;
  scale: number;
  values: string;
  dur: number; // SMIL <animate> dur — not affected by IDLE_SPEED
  blur2: string;
};

type CloudShape = {cx: number; cy: number; rx: number; ry: number; warm?: boolean; rot: number};

type Cloud = {
  top: string;
  width: string;
  cx: string;
  co: number;
  ind: number;
  bdur: number;
  vbW: number;
  vbH: number;
  filter: CloudFilterDef;
  shapes: CloudShape[];
};

const CLOUDS: Cloud[] = [
  {
    top: "41%",
    width: "min(150vw, 1500px)",
    cx: "46%",
    co: 0.92,
    ind: 0.2,
    bdur: 17,
    vbW: 320,
    vbH: 130,
    filter: {
      id: "ls-cr1",
      blur1: "7 2",
      bf1: "0.006 0.055",
      seed1: 8,
      mAlpha: "1.7 -0.22",
      bf2: "0.008 0.06",
      seed2: 3,
      scale: 26,
      values: "26;32;28;30;26",
      dur: 26,
      blur2: "3 1",
    },
    shapes: [
      {cx: 150, cy: 72, rx: 150, ry: 15, warm: true, rot: -1},
      {cx: 120, cy: 58, rx: 118, ry: 12, rot: -3},
      {cx: 180, cy: 48, rx: 96, ry: 10, rot: -4},
      {cx: 160, cy: 84, rx: 128, ry: 12, rot: 1},
      {cx: 90, cy: 66, rx: 78, ry: 8, warm: true, rot: -2},
      {cx: 232, cy: 62, rx: 82, ry: 8, rot: -3},
      {cx: 200, cy: 94, rx: 96, ry: 8, warm: true, rot: 2},
    ],
  },
  {
    top: "48%",
    width: "min(100vw, 980px)",
    cx: "22%",
    co: 0.82,
    ind: 1.0,
    bdur: 22,
    vbW: 260,
    vbH: 105,
    filter: {
      id: "ls-cr2",
      blur1: "6 2",
      bf1: "0.007 0.058",
      seed1: 4,
      mAlpha: "1.6 -0.2",
      bf2: "0.009 0.064",
      seed2: 17,
      scale: 22,
      values: "22;28;24;26;22",
      dur: 22,
      blur2: "2.6 0.9",
    },
    shapes: [
      {cx: 120, cy: 58, rx: 118, ry: 9, warm: true, rot: 1},
      {cx: 96, cy: 46, rx: 86, ry: 7, rot: -3},
      {cx: 150, cy: 42, rx: 80, ry: 6, rot: -2},
      {cx: 134, cy: 68, rx: 100, ry: 7, rot: 2},
      {cx: 186, cy: 54, rx: 66, ry: 5, warm: true, rot: -2},
    ],
  },
  {
    top: "62%",
    width: "min(94vw, 900px)",
    cx: "76%",
    co: 0.74,
    ind: 1.6,
    bdur: 19,
    vbW: 240,
    vbH: 95,
    filter: {
      id: "ls-cr3",
      blur1: "5.5 1.8",
      bf1: "0.008 0.06",
      seed1: 23,
      mAlpha: "1.6 -0.2",
      bf2: "0.01 0.066",
      seed2: 9,
      scale: 20,
      values: "20;26;22;24;20",
      dur: 30,
      blur2: "2.4 0.8",
    },
    shapes: [
      {cx: 110, cy: 50, rx: 104, ry: 4, warm: true, rot: -2},
      {cx: 86, cy: 40, rx: 74, ry: 3, rot: -4},
      {cx: 140, cy: 42, rx: 72, ry: 3, rot: -3},
      {cx: 122, cy: 60, rx: 88, ry: 3, rot: 1},
    ],
  },
  {
    top: "30%",
    width: "min(84vw, 820px)",
    cx: "64%",
    co: 0.82,
    ind: 0.6,
    bdur: 25,
    vbW: 220,
    vbH: 90,
    filter: {
      id: "ls-cr4",
      blur1: "5 1.6",
      bf1: "0.009 0.062",
      seed1: 31,
      mAlpha: "1.55 -0.18",
      bf2: "0.011 0.068",
      seed2: 27,
      scale: 18,
      values: "18;24;20;22;18",
      dur: 34,
      blur2: "2.2 0.7",
    },
    shapes: [
      {cx: 104, cy: 50, rx: 96, ry: 3.5, warm: true, rot: 2},
      {cx: 82, cy: 42, rx: 66, ry: 3, rot: -3},
      {cx: 138, cy: 44, rx: 66, ry: 2.8, rot: 3},
      {cx: 116, cy: 58, rx: 82, ry: 2.8, rot: 1},
    ],
  },
  {
    top: "71%",
    width: "min(120vw, 1180px)",
    cx: "38%",
    co: 0.66,
    ind: 1.3,
    bdur: 21,
    vbW: 300,
    vbH: 110,
    filter: {
      id: "ls-cr5",
      blur1: "6 2",
      bf1: "0.007 0.057",
      seed1: 14,
      mAlpha: "1.6 -0.2",
      bf2: "0.009 0.063",
      seed2: 21,
      scale: 22,
      values: "22;28;24;26;22",
      dur: 28,
      blur2: "2.6 0.9",
    },
    shapes: [
      {cx: 148, cy: 62, rx: 142, ry: 14, warm: true, rot: -1},
      {cx: 110, cy: 50, rx: 100, ry: 11, rot: -3},
      {cx: 186, cy: 48, rx: 92, ry: 10, rot: -2},
      {cx: 160, cy: 74, rx: 118, ry: 11, rot: 1},
      {cx: 86, cy: 64, rx: 74, ry: 8, warm: true, rot: -2},
      {cx: 224, cy: 64, rx: 80, ry: 8, rot: -2},
    ],
  },
];

const CloudFilter = ({f}: {f: CloudFilterDef}) => (
  <filter id={f.id} x="-30%" y="-70%" width="160%" height="240%" colorInterpolationFilters="sRGB">
    <feGaussianBlur in="SourceGraphic" stdDeviation={f.blur1} result="soft" />
    <feTurbulence type="fractalNoise" baseFrequency={f.bf1} numOctaves={4} seed={f.seed1} result="n1" />
    <feColorMatrix in="n1" type="matrix" values={`0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 ${f.mAlpha}`} result="dens" />
    <feComposite in="dens" in2="soft" operator="arithmetic" k1={1} k2={0} k3={0} k4={0} result="tex" />
    <feTurbulence type="fractalNoise" baseFrequency={f.bf2} numOctaves={2} seed={f.seed2} result="n2" />
    {/* Static displacement — the texture rasterizes once. Animating `scale` here
        (per-frame SVG filter re-raster) was the single biggest mobile cost. */}
    <feDisplacementMap in="tex" in2="n2" scale={f.scale} xChannelSelector="R" yChannelSelector="G" />
    <feGaussianBlur stdDeviation={f.blur2} />
  </filter>
);

// ————— fog: huge blurred wisps twirling slowly, density pulsing in and out —————
type FogSwirl = {
  left: string;
  top: string;
  spin: string;
  color: string;
  w: string;
  h: string;
  ox: string;
  oy: string;
  fa: number;
  fb: number;
  pd: number;
};

const FOG: FogSwirl[] = [
  {
    left: "40%",
    top: "36%",
    spin: `ls-fogSpin ${d(64)} ${stepFor(64)} infinite`,
    color: `color-mix(in srgb, ${palette.mist} 88%, transparent)`,
    w: "110vmin",
    h: "64vmin",
    ox: "9vmin",
    oy: "-5vmin",
    fa: 0.5,
    fb: 0.26,
    pd: 37,
  },
  {
    left: "84%",
    top: "14%",
    spin: `ls-fogSpin ${d(50)} ${stepFor(50)} infinite reverse`,
    color: `color-mix(in srgb, ${palette.duskLow} 82%, transparent)`,
    w: "95vmin",
    h: "60vmin",
    ox: "-10vmin",
    oy: "-5vmin",
    fa: 0.3,
    fb: 0.42,
    pd: 29,
  },
  {
    left: "50%",
    top: "36%",
    spin: `ls-fogSway ${d(17)} ${stepFor(17)} infinite alternate`,
    color: "rgba(255,255,255,0.78)",
    w: "90vmin",
    h: "52vmin",
    ox: "9vmin",
    oy: "6vmin",
    fa: 0.12,
    fb: 0.3,
    pd: 43,
  },
  {
    left: "4%",
    top: "58%",
    spin: `ls-fogSway ${d(23)} ${stepFor(23)} infinite alternate-reverse`,
    color: `color-mix(in srgb, ${palette.duskTop} 55%, transparent)`,
    w: "84vmin",
    h: "56vmin",
    ox: "9vmin",
    oy: "-5vmin",
    fa: 0.38,
    fb: 0.16,
    pd: 31,
  },
  {
    left: "94%",
    top: "62%",
    spin: `ls-fogSpin ${d(74)} ${stepFor(74)} infinite`,
    color: `color-mix(in srgb, ${palette.mist} 75%, transparent)`,
    w: "88vmin",
    h: "54vmin",
    ox: "-8vmin",
    oy: "-5vmin",
    fa: 0.32,
    fb: 0.5,
    pd: 53,
  },
];

const RAYS = [
  {width: "11vmin", rotate: 14, anim: `ls-rayWax ${d(13)} ${stepFor(13)} infinite`},
  {width: "9vmin", rotate: -19, anim: `ls-rayWax ${d(19)} ${stepFor(19)} ${d(5)} infinite`},
  {width: "13vmin", rotate: -3, anim: `ls-rayWax ${d(23)} ${stepFor(23)} ${d(9)} infinite`},
];

export const BreathingLandscape = ({paused = false}: {paused?: boolean} = {}) => {
  const [reduced] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
  const [rootRef, inView] = useInView<HTMLDivElement>();
  const animate = inView && !reduced && !paused;
  const motion = (a: string): string | undefined => (animate ? a : undefined);

  // The cloud strata (turbulence/displacement filters) and the blurred fog are
  // the only raster-heavy layers — mounting them rasterizes in one long task. We
  // hold them back until the browser is idle so they never land on top of the
  // splash or the welcome entrance animations (which then "stick"). The cheap
  // base (gradient, sun, rays, hills, mist) renders immediately; clouds fade in.
  const [heavy, setHeavy] = useState(reduced);
  useEffect(() => {
    if (reduced || paused || heavy) return;
    const ric = typeof window !== "undefined" ? window.requestIdleCallback : undefined;
    if (ric) {
      const idleId = ric(() => setHeavy(true), {timeout: 1200});
      return () => window.cancelIdleCallback?.(idleId);
    }
    const timer = window.setTimeout(() => setHeavy(true), 900);
    return () => clearTimeout(timer);
  }, [reduced, paused, heavy]);

  return (
    <div
      ref={rootRef}
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        background: `linear-gradient(180deg, ${palette.duskTop} 0%, ${palette.duskMid} 34%, ${palette.duskDeep} 62%, ${palette.duskLow} 80%, ${palette.horizon} 94%)`,
      }}
    >
      <style>{KEYFRAMES}</style>

      <div
        style={{
          position: "absolute",
          inset: 0,
          transformOrigin: "50% 62%",
          animation: motion(`ls-sceneBreathe ${d(16)} ${stepFor(16)} infinite alternate`),
          willChange: "transform",
        }}
      >
        {/* Shifting light — slow warm/cool wash across the sky */}
        <div
          style={{
            position: "absolute",
            left: "-10%",
            right: "-10%",
            top: "-10%",
            bottom: "28%",
            zIndex: 2,
            pointerEvents: "none",
            opacity: 0.22,
            background: `radial-gradient(ellipse 60% 50% at 70% 62%, color-mix(in srgb, ${palette.horizon} 60%, transparent) 0%, transparent 70%), radial-gradient(ellipse 55% 48% at 26% 58%, color-mix(in srgb, ${palette.duskDeep} 55%, transparent) 0%, transparent 70%)`,
            filter: "blur(20px)",
            animation: motion(`ls-skyShift ${d(58)} ${stepFor(58)} infinite alternate`),
          }}
        />

        {/* Hazy upper sky — soft warm-white veil over the pastel blue */}
        <div
          style={{
            position: "absolute",
            left: "-6%",
            right: "-6%",
            top: 0,
            height: "44%",
            zIndex: 2,
            pointerEvents: "none",
            opacity: 0.45,
            background: `linear-gradient(180deg, color-mix(in srgb, ${palette.cloudHi} 0%, transparent) 0%, color-mix(in srgb, ${palette.cloudHi} 12%, transparent) 55%, color-mix(in srgb, ${palette.cloudHi} 24%, transparent) 86%, transparent 100%)`,
            filter: "blur(16px)",
            animation: motion(`ls-hazeBreathe ${d(32)} ${stepFor(32)} infinite alternate`),
          }}
        />

        {/* Sun — settled high, where the splash leaves it */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "46%",
            zIndex: 2,
            width: 0,
            height: 0,
            transform: "translateY(-28vh) scale(1.5)",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              translate: "-50% -50%",
              borderRadius: "50%",
              width: "120vmin",
              height: "120vmin",
              background:
                "radial-gradient(circle, rgba(255,255,255,0.85) 0%, rgba(252,253,255,0.45) 26%, rgba(238,242,250,0.18) 55%, rgba(238,242,250,0) 72%)",
              animation: motion(`ls-haloBreathe ${d(7)} ${stepFor(7)} infinite alternate`),
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              translate: "-50% -50%",
              borderRadius: "50%",
              width: "52vmin",
              height: "52vmin",
              background: `radial-gradient(circle, ${palette.sunGlow} 0%, rgba(255,255,255,0.72) 38%, rgba(255,255,255,0) 70%)`,
              filter: "blur(26px)",
              opacity: 0.55,
              animation: motion(`ls-glowBreathe ${d(9)} ${stepFor(9)} infinite alternate`),
            }}
          />
        </div>

        {/* Occasional light rays — delicate shafts that wax and wane */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "18vh",
            zIndex: 2,
            pointerEvents: "none",
            width: 0,
            height: 0,
          }}
        >
          {RAYS.map((r, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                translate: "-50% 0",
                width: r.width,
                height: "52vh",
                transformOrigin: "50% 0",
                transform: `rotate(${r.rotate}deg)`,
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.68) 0%, rgba(255,255,255,0.2) 55%, rgba(255,255,255,0) 100%)",
                clipPath: "polygon(40% 0, 60% 0, 88% 100%, 12% 100%)",
                filter: "blur(9px)",
                opacity: 0,
                animation: motion(r.anim),
              }}
            />
          ))}
        </div>

        {/* Cloud strata — soft masses, fade + rise into place then breathe.
            Deferred until idle (see `heavy`) so the turbulence filters don't
            rasterize over the splash/entrance. */}
        {heavy && (
          <div
            style={{position: "absolute", left: 0, right: 0, top: 0, bottom: "16%", zIndex: 2, pointerEvents: "none"}}
          >
            {CLOUDS.map((c, i) => (
              <div
                key={i}
                style={{
                  position: "absolute",
                  top: c.top,
                  left: c.cx,
                  width: c.width,
                  opacity: reduced ? c.co : 0,
                  transform: "translateX(-50%)",
                  willChange: "transform, opacity, scale",
                  ["--co" as string]: c.co,
                  animation: motion(
                    `ls-cloudIn ${d(3.2)} ease-out ${d(c.ind)} forwards, ls-cloudBreathe ${d(c.bdur)} ${stepFor(
                      c.bdur
                    )} ${d(c.ind + 3.2)} infinite`
                  ),
                }}
              >
                <svg
                  viewBox={`0 0 ${c.vbW} ${c.vbH}`}
                  preserveAspectRatio="xMidYMid meet"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    overflow: "visible",
                    transform: "scaleY(0.8)",
                    animation: motion(`ls-cloudRise ${d(3.2)} cubic-bezier(0.22, 1, 0.36, 1) ${d(c.ind)} both`),
                  }}
                >
                  <defs>
                    <CloudFilter f={c.filter} />
                  </defs>
                  <g filter={`url(#${c.filter.id})`}>
                    {c.shapes.map((s, j) => (
                      <ellipse
                        key={j}
                        cx={s.cx}
                        cy={s.cy}
                        rx={s.rx}
                        ry={s.ry}
                        fill={s.warm ? palette.cloudWarm : palette.cloudHi}
                        transform={`rotate(${s.rot} ${s.cx} ${s.cy})`}
                      />
                    ))}
                  </g>
                </svg>
              </div>
            ))}
          </div>
        )}

        {/* Fog — twirling wisps whose density keeps cycling. Deferred with the
            clouds (blurred wisps are also rasterization-heavy). */}
        {heavy && (
          <div
            style={{
              position: "absolute",
              left: "-12%",
              right: "-12%",
              top: "-12%",
              bottom: "16%",
              zIndex: 2,
              pointerEvents: "none",
            }}
          >
            {FOG.map((s, i) => (
              <div
                key={i}
                style={{position: "absolute", width: 0, height: 0, left: s.left, top: s.top, animation: motion(s.spin)}}
              >
                <div
                  style={{
                    position: "absolute",
                    left: s.ox,
                    top: s.oy,
                    width: s.w,
                    height: s.h,
                    translate: "-50% -50%",
                    borderRadius: "50%",
                    background: `radial-gradient(closest-side, ${s.color} 0%, transparent 72%)`,
                    filter: "blur(18px)",
                    opacity: s.fa,
                    willChange: "transform, opacity",
                    ["--fa" as string]: s.fa,
                    ["--fb" as string]: s.fb,
                    animation: motion(`ls-wispPulse ${d(s.pd)} ${stepFor(s.pd)} infinite alternate`),
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Hills — settled, faint crest lines */}
        <svg
          viewBox="0 0 390 260"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "30vh",
            zIndex: 3,
            display: "block",
            width: "100%",
          }}
        >
          <path d="M0 98 A 520 520 0 0 1 390 90 L390 260 L0 260 Z" fill={palette.hillFar} opacity={0.5} />
          <path
            d="M0 98 A 520 520 0 0 1 390 90"
            fill="none"
            stroke="rgba(255,249,238,0.95)"
            strokeWidth={1.4}
            vectorEffect="non-scaling-stroke"
            strokeLinecap="round"
            opacity={0.28}
            pathLength={1}
          />
          <path d="M0 150 A 340 340 0 0 1 390 142 L390 260 L0 260 Z" fill={palette.hillMid} opacity={0.68} />
          <path
            d="M0 150 A 340 340 0 0 1 390 142"
            fill="none"
            stroke="rgba(255,249,238,0.95)"
            strokeWidth={1.4}
            vectorEffect="non-scaling-stroke"
            strokeLinecap="round"
            opacity={0.28}
            pathLength={1}
          />
        </svg>

        {/* Mist — drifting gently across the valley */}
        <div
          style={{
            position: "absolute",
            left: "-4%",
            right: "-4%",
            bottom: "16vh",
            height: "12vh",
            zIndex: 4,
            background: `radial-gradient(ellipse 70% 100% at 50% 50%, color-mix(in srgb, ${palette.mist} 70%, transparent) 0%, transparent 70%)`,
            filter: "blur(10px)",
            animation: motion(`ls-mistDrift ${d(26)} ${stepFor(26)} infinite alternate`),
          }}
        />
      </div>
    </div>
  );
};
